<template>
  <div class="row">
    <div class="col-lg-2 mb-3">
      <div class="tab-title-divider d-md-block d-lg-block d-xl-block d-none d-sm-none">
        <span class='tab-title'>Instruments</span>
        <router-link class="float-right tab-add form-action-text text-secondary white-space-nowrap"
          :to="{ name: 'newInstrument', params: {
          project_id: $route.params.project_id}}"
        >
          + Add
        </router-link>
      </div>
      <LoadingSpinner v-if="instrumentsLoading" message ='Loading Instruments' size = '1x'>
      </LoadingSpinner>
      <!-- eslint-disable-next-line max-len -->
      <router-link class="btn btn-block btn-secondary btn-sm text-white mb-3 d-md-none d-lg-none d-xl-none d-block d-sm-block white-space-nowrap"
        :to="{ name: 'newInstrument', params: {
          project_id: $route.params.project_id}}"
        tag="button">
        + Add
      </router-link>
      <ul class="nav nav-pills flex-column" v-if="!instrumentsLoading">
        <li v-for='instrument in orderedInstruments'
          :key='instrument.id'
          class="nav-item">
          <router-link
          class="nav-link text-left py-1 px-2"
          :to="{ name: 'projectInstrument', params: {
          project_id: $route.params.project_id,
          id: instrument.id }}">
            <div class="d-flex flex-column">
              <div class="d-flex justify-content-between">
                <div :id="'tag-'+ instrument.id">
                  {{ getInstrumentTitle(instrument) }}
                </div>
                <div>
                  <font-awesome-icon
                    :id="'inst-tag-' + instrument.id"
                    v-if="instrument.tagTypeIds.includes(1)"
                    icon="exclamation-triangle"
                    class="important-tag-text mr-1"
                    size="sm"
                    :aria-label="`Important`"
                    :class="`important-tag`"
                  />
                  <b-tooltip :target="'inst-tag-' + instrument.id">
                    Important
                  </b-Tooltip>
                  <font-awesome-icon
                    :id="'inst-' + instrument.id"
                    icon="circle"
                    class="rounded-circle border border-white border-circle"
                    size="sm"
                    :aria-label="`Status: ${getStatusName(instrument.statusId)}`"
                    :class="getStatusClass(instrument.statusId, 'Instrument')" />
                  <b-tooltip :target="'inst-' + instrument.id">
                    {{getStatusName(instrument.statusId)}}
                  </b-Tooltip>
                </div>

                <b-tooltip :target="'tag-' + instrument.id" v-if="displayInstrumentTagHoverOver(instrument.tagTypeIds)">
                  {{getTagHoverOverText(instrument.tagTypeIds)}}
                </b-Tooltip>
              </div>
              <small
                :title="getInstrumentTypeName(instrument.typeId)"
                :id="'instType-' + instrument.id">
                  {{getInstrumentTypeProperty(instrument.typeId, 'code') || '&nbsp;'}}
              </small>
              <b-tooltip :target="'instType-' + instrument.id" placement="leftbottom"
               v-if="getInstrumentTypeName(instrument.typeId)">
                {{ getInstrumentTypeName(instrument.typeId) }}
              </b-Tooltip>
            </div>
          </router-link>
        </li>
      </ul>
    </div>
    <div :key="currentInstrument.id"
      class="col-lg-10">
      <div class="text-right expand-collapse">
        <b-link class="text-secondary" @click="expandAll">Expand All</b-link> |
        <b-link class="text-secondary" @click="collapseAll">Collapse All</b-link>
      </div>
      <InstrumentDetailsCard v-if="!isInstrumentEdit"
        ref="detailsCard"
      >
      </InstrumentDetailsCard>
      <EditInstrumentForm
                      v-if="isInstrumentEdit"
                      ref="instrumentDetails"
                      >
      </EditInstrumentForm>
      <AssociatedPropertiesCard :storeNamespace="associatedPropertiesNamespace"
        ref="associatedPropertiesCard"
      >
      </AssociatedPropertiesCard>
      <ConservationPurposesCard
        :instrumentId="currentInstrument.id"
        ref="conservationPurposesCard"
      >
      </ConservationPurposesCard>
      <BuildingActivityAreaCard
        :instrumentId="currentInstrument.id"
        ref="buildingActivityAreaCard">
      </BuildingActivityAreaCard>
      <WaterQualityProtectionAreaCard
        :instrumentId="currentInstrument.id"
        ref="waterQualityProtectionAreaCard"
      >
      </WaterQualityProtectionAreaCard>
      <PartnershipGrantCard
        :instrumentId="currentInstrument.id"
        :entities="currentInstrument.partnershipGrants"
        ref="partnershipGrantCard"
      >
      </PartnershipGrantCard>
      <ContactsCard
        :parentId="currentInstrument.id"
        :parentType="'instrument'"
        :parentTitle="'Instrument'"
        :roles="contactRoles"
        :rolesLoading="rolesLoading"
        :entities="contacts"
        :entitiesLoading="contactsLoading"
        ref="instrumentContactsCard">
      </ContactsCard>
      <ReviewCard
        :parentId="currentInstrument.id"
        :parentType="'instrument'"
        :parentTitle="'Instrument'"
        :canAssignReviews="true"
        :reviewStatuses="reviewStatuses"
        :reviewStatusesLoading="reviewStatusesLoading"
        :entities="reviews"
        :entitiesLoading="reviewsLoading"
        ref="instrumentReviewCard"
      >
      </ReviewCard>
      <EventCard
        :parentId="currentInstrument.id"
        :parentType="'instrument'"
        :parentTitle="'Instrument'"
        :eventTypes="instrumentEventTypes"
        :eventTypesLoading="instrumentEventTypesLoading"
        :entities="events"
        :entitiesLoading="eventsLoading"
        ref="instrumentEventCard"
      >
      </EventCard>
      <BoxCard
        :folderId="currentInstrument.documentFolderId"
        :parentId="currentInstrument.id"
        storeNamespace="Instrument"
        ref="instrumentBoxCard"
      />
      <ConservationValuesCard
        :conservationValuesStatement="currentInstrument.conservationValuesStatement"
        ref="conservationValuesCard">
      </ConservationValuesCard>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { LookupModules, EntityModules } from '@/store/moduleConfig';
import RouterCheck from '@/mixins/RouterCheck';
import AssociatedPropertiesCard from '../components/associatedProperties/AssociatedPropertiesCard.vue';
import BoxCard from '../components/common/BoxCard.vue';
import ContactsCard from '../components/contacts/ContactsCard.vue';
import EditInstrumentForm from '../components/instrument/EditInstrumentForm.vue';
import EventCard from '../components/event/EventCard.vue';
import PartnershipGrantCard from '../components/partnershipGrant/PartnershipGrantCard.vue';
import ConservationPurposesCard from '../components/conservationPurposes/ConservationPurposesCard.vue';
import BuildingActivityAreaCard from '../components/specificTerms/BuildingActivityAreaCard.vue';
import WaterQualityProtectionAreaCard from '../components/waterQualityProtectionArea/ProtectionAreaCard.vue';
import InstrumentDetailsCard from '../components/instrument/InstrumentDetailsCard.vue';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import ConservationValuesCard from '../components/instrument/ConservationValuesCard.vue';
import ReviewCard from '../components/reviews/ReviewCard.vue';
import StatusHelper from '../mixins/StatusHelper';

export default {
  name: 'ProjectInstruments',
  mixins: [RouterCheck, StatusHelper],
  components: {
    AssociatedPropertiesCard,
    BoxCard,
    ContactsCard,
    EditInstrumentForm,
    EventCard,
    PartnershipGrantCard,
    ConservationPurposesCard,
    InstrumentDetailsCard,
    BuildingActivityAreaCard,
    WaterQualityProtectionAreaCard,
    LoadingSpinner,
    ConservationValuesCard,
    ReviewCard,
  },
  data() {
    return {
      associatedPropertiesNamespace: EntityModules.ASSOCIATED_PROPERTIES.namespace,
    };
  },
  created() {
    this.$store.dispatch('Instrument/fetchInstruments', parseInt(this.$route.params.project_id, 10))
      .then(() => {
        if (this.$route.name === 'projectInstruments') {
          this.pushDefaultRoute();
        } else {
          this.dispatchToFetchInstrumentAndChildren();
        }
      });
    this.$store.dispatch(`${LookupModules.AGREEMENT_TYPES.namespace}/fetchOptions`,
      LookupModules.AGREEMENT_TYPES.url);
    this.$store.dispatch(`${LookupModules.INSTRUMENT_TYPES.namespace}/fetchOptions`,
      LookupModules.INSTRUMENT_TYPES.url);
    this.$store.dispatch(`${LookupModules.INSTRUMENT_ROLES.namespace}/fetchOptions`,
      LookupModules.INSTRUMENT_ROLES.url);
    this.$store.dispatch(`${LookupModules.INSTRUMENT_STATUSES.namespace}/fetchOptions`,
      LookupModules.INSTRUMENT_STATUSES.url);
    this.$store.dispatch(`${LookupModules.SPECIFIC_TERMS.namespace}/fetchOptions`,
      LookupModules.SPECIFIC_TERMS.url);
    this.$store.dispatch(`${LookupModules.INSTRUMENT_EVENT_TYPES.namespace}/fetchOptions`,
      LookupModules.INSTRUMENT_EVENT_TYPES.url);
    this.$store.dispatch(`${LookupModules.REVIEW_STATUSES.namespace}/fetchOptions`,
      LookupModules.REVIEW_STATUSES.url);
    this.$store.dispatch(`${LookupModules.TAG_TYPES.namespace}/fetchOptions`,
      LookupModules.TAG_TYPES.url);
  },
  computed: {
    ...mapState(EntityModules.INSTRUMENT_CONTACT.namespace, {
      contacts: state => state.entities,
      contactsLoading: state => state.loading,
    }),
    ...mapState(EntityModules.INSTRUMENT_EVENT.namespace, {
      events: state => state.entities,
      eventsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.INSTRUMENT_STATUSES.namespace, {
      statusLoading: state => state.loading,
    }),
    ...mapState(LookupModules.INSTRUMENT_ROLES.namespace, {
      rolesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.INSTRUMENT_TYPES.namespace, {
      instrumentTypeLoading: state => state.loading,
    }),
    ...mapState(LookupModules.INSTRUMENT_EVENT_TYPES.namespace, {
      instrumentEventTypesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.REVIEW_STATUSES.namespace, {
      reviewStatusesLoading: state => state.loading,
    }),
    ...mapState(LookupModules.TAG_TYPES.namespace, {
      tagTypesLoading: state => state.loading,
    }),
    ...mapState(EntityModules.INSTRUMENT_REVIEW.namespace, {
      reviews: state => state.entities,
      reviewsLoading: state => state.loading,
    }),
    ...mapState('Instrument', {
      instrumentsLoading: state => state.instrumentsLoading,
      isInstrumentEdit: state => state.isInstrumentEdit,
      currentInstrument: state => state.currentInstrument,
    }),
    ...mapGetters(LookupModules.INSTRUMENT_STATUSES.namespace, {
      getStatusName: 'getName',
      instrumentStatusOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.INSTRUMENT_TYPES.namespace, {
      getInstrumentTypeName: 'getName',
      getInstrumentTypeProperty: 'getProperty',
      instrumentTypeOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.INSTRUMENT_ROLES.namespace, {
      contactRoles: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.INSTRUMENT_EVENT_TYPES.namespace, {
      instrumentEventTypes: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.REVIEW_STATUSES.namespace, {
      reviewStatuses: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.TAG_TYPES.namespace, {
      tagTypes: 'getActiveOptions',
    }),
    ...mapGetters('Instrument', ['orderedInstruments', 'getDefaultInstrument']),
  },
  watch: {
    $route(to) {
      if (to.name === 'projectInstruments') {
        this.pushDefaultRoute();
      } else {
        this.dispatchToFetchInstrumentAndChildren();
      }
    },
  },
  methods: {
    displayInstrumentTagHoverOver(tagTypeIds) {
      if (tagTypeIds && tagTypeIds.length === 1 && tagTypeIds[0] === 1) return false;
      if (tagTypeIds && tagTypeIds.length > 0) return true;
      return false;
    },
    getCardRefs() {
      const refs = [this.$refs.associatedPropertiesCard,
        this.$refs.instrumentEventCard,
        this.$refs.buildingActivityAreaCard,
        this.$refs.waterQualityProtectionAreaCard,
        this.$refs.partnershipGrantCard,
        this.$refs.instrumentContactsCard,
        this.$refs.instrumentReviewCard,
        this.$refs.instrumentBoxCard,
        this.$refs.conservationValuesCard,
        this.$refs.conservationPurposesCard,
      ];
      if (this.isInstrumentEdit) {
        refs.push(this.$refs.instrumentDetails);
      } else {
        refs.push(this.$refs.detailsCard);
      }
      return refs;
    },
    expandAll() {
      this.getCardRefs().forEach((ref) => {
        ref.forceExpand();
      });
    },
    collapseAll() {
      this.getCardRefs().forEach((ref) => {
        ref.forceCollapse();
      });
    },
    pushDefaultRoute() {
      this.$router.push({
        name: 'projectInstrument',
        params: {
          project_id: parseInt(this.$route.params.project_id, 10),
          id: this.getDefaultInstrument.id,
        },
      });
    },
    dispatchToFetchInstrumentAndChildren() {
      this.$store.dispatch('Instrument/fetchInstrument',
        {
          projectId: parseInt(this.$route.params.project_id, 10),
          id: parseInt(this.$route.params.id, 10),
        })
        .then(() => {
          // the following are only being loaded in this clause to ensure they fire
          // after the instrument has been retrieved
          this.$store.dispatch(`${EntityModules.ASSOCIATED_PROPERTIES.namespace}/fetchEntities`, {
            parentType: EntityModules.ASSOCIATED_PROPERTIES.parentType,
            parentId: this.currentInstrument.id,
            entityType: EntityModules.ASSOCIATED_PROPERTIES.entityType,
          });
          this.$store.dispatch(`${EntityModules.INSTRUMENT_CONTACT.namespace}/fetchEntities`, {
            parentType: EntityModules.INSTRUMENT_CONTACT.parentType,
            parentId: this.currentInstrument.id,
            entityType: EntityModules.INSTRUMENT_CONTACT.entityType,
          });
          this.$store.dispatch(`${EntityModules.CONSERVATION_PURPOSE.namespace}/fetchEntities`, {
            parentType: EntityModules.CONSERVATION_PURPOSE.parentType,
            parentId: this.currentInstrument.id,
            entityType: EntityModules.CONSERVATION_PURPOSE.entityType,
          });
          this.$store.dispatch(`${EntityModules.INSTRUMENT_EVENT.namespace}/fetchEntities`, {
            parentType: EntityModules.INSTRUMENT_EVENT.parentType,
            parentId: this.currentInstrument.id,
            entityType: EntityModules.INSTRUMENT_EVENT.entityType,
          });
          this.$store.dispatch(`${EntityModules.PARTNERSHIP_GRANT.namespace}/fetchEntities`, {
            parentType: EntityModules.PARTNERSHIP_GRANT.parentType,
            parentId: this.currentInstrument.id,
            entityType: EntityModules.PARTNERSHIP_GRANT.entityType,
          });
          this.$store.dispatch(`${EntityModules.BUILDING_ACTIVITY_AREA.namespace}/fetchEntities`, {
            parentType: EntityModules.BUILDING_ACTIVITY_AREA.parentType,
            parentId: this.currentInstrument.id,
            entityType: EntityModules.BUILDING_ACTIVITY_AREA.entityType,
          });
          this.$store.dispatch(`${EntityModules.WATER_QUALITY_PROTECTION_AREA.namespace}/fetchEntities`, {
            parentType: EntityModules.WATER_QUALITY_PROTECTION_AREA.parentType,
            parentId: this.currentInstrument.id,
            entityType: EntityModules.WATER_QUALITY_PROTECTION_AREA.entityType,
          });
          this.$store.dispatch(`${EntityModules.INSTRUMENT_REVIEW.namespace}/fetchEntities`, {
            parentType: EntityModules.INSTRUMENT_REVIEW.parentType,
            parentId: this.currentInstrument.id,
            entityType: EntityModules.INSTRUMENT_REVIEW.entityType,
          });
        });
    },
    getTagHoverOverText(tagTypeIds) {
      let tagHoverText = '';
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < tagTypeIds.length; i++) {
        const tagId = tagTypeIds[i];
        const tag = this.tagTypes.find(x => x.id === tagId);
        // do NOT include Important Tag in hover
        if (tag && tagId !== 1) {
          if (i === 0 || tagHoverText === '') {
            tagHoverText += `${tag.name}`;
          } else {
            tagHoverText += ` - ${tag.name}`;
          }
        }
      }
      return tagHoverText;
    },
    getInstrumentTitle(instrument) {
      if (instrument.executedDate) {
        return `${instrument.id} (${this.$options.filters.yearFromDateTimeString(instrument.executedDate)})`;
      }
      if (instrument.recordedDate) {
        return `${instrument.id} (${this.$options.filters.yearFromDateTimeString(instrument.recordedDate)})`;
      }
      return `${instrument.id}`;
    },
  },
};
</script>
